import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useState } from 'react';
import { track } from '@/lib/analytics';
import { ADMIN_PACIFIC_TZ } from '@/lib/adminPacificTime';
import { apiJson } from '@/lib/api';
import type {
  AdminDashboardMetricsResponse,
  AdminUserRow,
  AdminUsersResponse,
} from '@/lib/types';

const PAGE_SIZE = 50;

type AdminUsersSortKey =
  | 'id'
  | 'name'
  | 'email'
  | 'phone'
  | 'created_at'
  | 'subscribed'
  | 'deleted'
  | 'messages'
  | 'expenses';

function defaultSortDirForColumn(key: AdminUsersSortKey): 'asc' | 'desc' {
  if (key === 'name' || key === 'email' || key === 'phone') return 'asc';
  return 'desc';
}

// Phone numbers are stored as E.164 (+1XXXXXXXXXX), but admins often paste
// formatted numbers like "(484) 695-8563" (sometimes with unicode dashes).
// When the query looks like a phone (enough digits, no letters/email), strip
// non-digits so the server's ILIKE substring match still hits the stored value.
function normalizeAdminSearchQuery(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (trimmed.includes('@')) return trimmed;
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length >= 7 && !/[a-zA-Z]/.test(trimmed)) {
    return digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  }
  return trimmed;
}

function SortTh({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
  title,
}: {
  label: string;
  sortKey: AdminUsersSortKey;
  activeKey: AdminUsersSortKey;
  dir: 'asc' | 'desc';
  onSort: (key: AdminUsersSortKey) => void;
  align?: 'left' | 'right';
  title?: string;
}) {
  const active = activeKey === sortKey;
  const alignClass = align === 'right' ? 'text-right' : 'text-left';
  return (
    <th
      scope='col'
      title={title}
      className={`px-4 py-3 ${alignClass} ${align === 'right' ? 'tabular-nums' : ''}`}
    >
      <button
        type='button'
        onClick={() => onSort(sortKey)}
        aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={`group inline-flex w-full items-center gap-1 font-semibold uppercase tracking-wide text-gray-600 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm ${align === 'right' ? 'justify-end' : 'justify-start'}`}
      >
        <span>{label}</span>
        {active ? (
          <span className='text-primary tabular-nums' aria-hidden>
            {dir === 'asc' ? '↑' : '↓'}
          </span>
        ) : (
          <span className='text-gray-300 opacity-0 group-hover:opacity-100' aria-hidden>
            ↕
          </span>
        )}
      </button>
    </th>
  );
}

const USER_VIEW_LINK_CLASS =
  'inline-flex px-2 py-1 rounded-md border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-100';

const MOBILE_SORT_OPTIONS: Array<[AdminUsersSortKey, string]> = [
  ['created_at', 'Created'],
  ['id', 'ID'],
  ['name', 'Name'],
  ['email', 'Email'],
  ['phone', 'Phone'],
  ['subscribed', 'Subscribed'],
  ['deleted', 'Deleted'],
  ['messages', 'Messages'],
  ['expenses', 'Expenses'],
];

function userDisplayName(u: AdminUserRow): string {
  return [u.first_name, u.last_name].filter(Boolean).join(' ') || '—';
}

function AdminUserCard({ u }: { u: AdminUserRow }) {
  const name = userDisplayName(u);
  const created = new Date(u.created_at).toLocaleDateString(undefined, {
    timeZone: ADMIN_PACIFIC_TZ,
  });
  return (
    <Link
      to='/admin/users/$userId'
      params={{ userId: String(u.id) }}
      search={{ hid: undefined }}
      onClick={() => track('Admin User Row Clicked', { source: 'card' })}
      className='block rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:bg-gray-50 active:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2'
    >
      <div className='flex items-start justify-between gap-3'>
        <div className='min-w-0'>
          <p className='truncate font-medium text-gray-900'>{name}</p>
          <p className='truncate text-xs text-gray-500'>{u.email ?? '—'}</p>
          {u.phone_number ? (
            <p className='truncate text-xs text-gray-500'>{u.phone_number}</p>
          ) : null}
          <p className='mt-1 text-[11px] tabular-nums text-gray-400'>
            #{u.id} · {created}
          </p>
        </div>
        <span className={`${USER_VIEW_LINK_CLASS} pointer-events-none shrink-0`}>
          View
        </span>
      </div>
      <div className='mt-3 flex flex-wrap items-center gap-1.5'>
        {u.is_professional ? (
          <span className='inline-flex rounded-full bg-violet-100 px-2 py-0.5 text-xs font-semibold text-violet-800'>
            Pro
          </span>
        ) : null}
        {u.calm_filter_enabled ? (
          <span className='inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800'>
            Calm
          </span>
        ) : null}
        {u.subscribed ? (
          <span className='inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700'>
            Subscribed{u.subscription_override ? ' (override)' : ''}
          </span>
        ) : (
          <span className='inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-500'>
            Not subscribed
          </span>
        )}
        {u.deleted_at ? (
          <span className='inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700'>
            Deleted
          </span>
        ) : null}
        <span className='text-xs tabular-nums text-gray-500'>
          {u.household_message_count.toLocaleString()} msgs ·{' '}
          {u.household_expense_count.toLocaleString()} expenses
        </span>
      </div>
    </Link>
  );
}

function AdminUsersPage() {
  const { getToken } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [subscribed, setSubscribed] = useState<'all' | 'yes' | 'no'>('all');
  const [professionalOnly, setProfessionalOnly] = useState(false);
  const [calmFilterOnly, setCalmFilterOnly] = useState(false);
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(0);
  const [sortBy, setSortBy] = useState<AdminUsersSortKey>('created_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const onSortColumn = (key: AdminUsersSortKey) => {
    setPage(0);
    if (sortBy === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDir(defaultSortDirForColumn(key));
    }
  };

  const params = new URLSearchParams();
  const normalizedQ = normalizeAdminSearchQuery(q);
  if (normalizedQ) params.set('q', normalizedQ);
  if (subscribed === 'yes') params.set('subscribed', 'true');
  if (subscribed === 'no') params.set('subscribed', 'false');
  if (professionalOnly) params.set('professional_only', 'true');
  if (calmFilterOnly) params.set('calm_filter_enabled', 'true');
  if (includeDeleted) params.set('include_deleted', 'true');
  params.set('limit', String(PAGE_SIZE));
  params.set('offset', String(page * PAGE_SIZE));
  params.set('sort_by', sortBy);
  params.set('sort_dir', sortDir);

  const usersQuery = useQuery({
    queryKey: ['admin', 'users', params.toString()],
    queryFn: () =>
      apiJson<AdminUsersResponse>(getToken, `/v1/admin/users?${params.toString()}`),
    placeholderData: keepPreviousData,
  });

  const metricsQuery = useQuery({
    queryKey: ['admin', 'metrics'],
    queryFn: () => apiJson<AdminDashboardMetricsResponse>(getToken, '/v1/admin/metrics'),
  });

  const data = usersQuery.data;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const usd = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  });

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3'>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Total users
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.total_user_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>Accounts not deleted</p>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Professional users
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.professional_user_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>
            Email-only accounts with no phone (matches Pro web sign-up)
          </p>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Subscribed users
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.subscribed_user_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>
            Active subscription or admin override
          </p>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Professional invites pending
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.professional_invite_pending_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>
            Email invite sent; professional has not accepted yet
          </p>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Professional invites accepted
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.professional_invite_accepted_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>
            Professional accepted the invite (connected households)
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3'>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Expenses (app-wide)
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.expense_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>Non-deleted expense records</p>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Amount tracked
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {usd.format((metricsQuery.data?.expense_amount_cents ?? 0) / 100)}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>Sum of expense amounts (USD)</p>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Journal entries
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.journal_entry_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>Non-deleted entries (app-wide)</p>
        </div>
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'>
          <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Messages
          </p>
          {metricsQuery.isLoading ? (
            <p className='mt-1 text-2xl font-semibold text-gray-300'>—</p>
          ) : metricsQuery.isError ? (
            <p className='mt-1 text-sm text-red-600'>Could not load</p>
          ) : (
            <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
              {(metricsQuery.data?.message_count ?? 0).toLocaleString()}
            </p>
          )}
          <p className='mt-1 text-xs text-gray-500'>
            Household chat only (excludes dispute-thread messages)
          </p>
        </div>
      </div>

      <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex flex-wrap gap-3 items-end'>
        <div className='flex-1 min-w-[240px]'>
          <label className='block text-xs font-semibold text-gray-600 mb-1'>
            Search (name, email, phone, user ID)
          </label>
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
            placeholder='e.g. jane@example.com or 12345'
            className='w-full px-3 py-2 border border-gray-300 rounded-md text-sm'
          />
        </div>
        <div>
          <label className='block text-xs font-semibold text-gray-600 mb-1'>
            Subscribed
          </label>
          <select
            value={subscribed}
            onChange={(e) => {
              setSubscribed(e.target.value as 'all' | 'yes' | 'no');
              setPage(0);
            }}
            className='px-3 py-2 border border-gray-300 rounded-md text-sm'
          >
            <option value='all'>All</option>
            <option value='yes'>Subscribed</option>
            <option value='no'>Not subscribed</option>
          </select>
        </div>
        <label
          className='flex items-center gap-2 text-sm text-gray-700'
          title='Email-only accounts with no phone on file'
        >
          <input
            type='checkbox'
            checked={professionalOnly}
            onChange={(e) => {
              setProfessionalOnly(e.target.checked);
              setPage(0);
            }}
          />
          Professionals only
        </label>
        <label
          className='flex items-center gap-2 text-sm text-gray-700'
          title='Users who turned on Calm Filter'
        >
          <input
            type='checkbox'
            checked={calmFilterOnly}
            onChange={(e) => {
              setCalmFilterOnly(e.target.checked);
              setPage(0);
            }}
          />
          Calm filter on
        </label>
        <label className='flex items-center gap-2 text-sm text-gray-700'>
          <input
            type='checkbox'
            checked={includeDeleted}
            onChange={(e) => {
              setIncludeDeleted(e.target.checked);
              setPage(0);
            }}
          />
          Include deleted
        </label>
        <div className='flex items-center gap-2 lg:hidden'>
          <label className='text-xs font-semibold text-gray-600'>
            Sort
            <select
              value={sortBy}
              onChange={(e) => {
                const next = e.target.value as AdminUsersSortKey;
                setPage(0);
                setSortBy(next);
                setSortDir(defaultSortDirForColumn(next));
              }}
              className='ml-2 px-3 py-2 border border-gray-300 rounded-md text-sm font-normal text-gray-900'
            >
              {MOBILE_SORT_OPTIONS.map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <button
            type='button'
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className='px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700'
            aria-label={sortDir === 'asc' ? 'Sort ascending' : 'Sort descending'}
          >
            {sortDir === 'asc' ? '↑' : '↓'}
          </button>
        </div>
      </div>

      {usersQuery.isLoading && (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-8 text-center text-gray-400 lg:hidden'>
          Loading…
        </div>
      )}
      {usersQuery.isError && (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-8 text-center text-red-500 lg:hidden'>
          Failed to load users.
        </div>
      )}
      {data && data.users.length === 0 && !usersQuery.isLoading && (
        <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-8 text-center text-gray-400 lg:hidden'>
          No users match.
        </div>
      )}
      {data && data.users.length > 0 && (
        <div className='space-y-3 lg:hidden'>
          {data.users.map((u) => (
            <AdminUserCard key={u.id} u={u} />
          ))}
        </div>
      )}

      <div className='hidden bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto lg:block'>
        <table className='w-full text-sm'>
          <thead className='bg-gray-50 text-xs uppercase text-gray-500'>
            <tr>
              <SortTh
                label='ID'
                sortKey='id'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Name'
                sortKey='name'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Email'
                sortKey='email'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Phone'
                sortKey='phone'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <th
                scope='col'
                className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600'
                title='Email-only sign-up with no phone on file'
              >
                Pro
              </th>
              <th
                scope='col'
                className='px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600'
                title='Calm Filter enabled in app settings'
              >
                Calm
              </th>
              <SortTh
                label='Created'
                sortKey='created_at'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Subscribed'
                sortKey='subscribed'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Deleted'
                sortKey='deleted'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
              />
              <SortTh
                label='Messages'
                sortKey='messages'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
                align='right'
                title='Non-dispute chat messages in all households this user belongs to'
              />
              <SortTh
                label='Expenses'
                sortKey='expenses'
                activeKey={sortBy}
                dir={sortDir}
                onSort={onSortColumn}
                align='right'
                title='Non-deleted expenses in those households'
              />
              <th scope='col' className='px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600'>
                Actions
              </th>
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-100'>
            {usersQuery.isLoading && (
              <tr>
                <td colSpan={12} className='px-4 py-8 text-center text-gray-400'>
                  Loading…
                </td>
              </tr>
            )}
            {usersQuery.isError && (
              <tr>
                <td colSpan={12} className='px-4 py-8 text-center text-red-500'>
                  Failed to load users.
                </td>
              </tr>
            )}
            {data?.users.map((u) => (
              <tr
                key={u.id}
                className='cursor-pointer hover:bg-gray-50'
                onClick={() => {
                  track('Admin User Row Clicked', { source: 'table' });
                  navigate({
                    to: '/admin/users/$userId',
                    params: { userId: String(u.id) },
                    search: { hid: undefined },
                  });
                }}
              >
                <td className='cursor-pointer px-4 py-2 text-gray-500'>{u.id}</td>
                <td className='cursor-pointer px-4 py-2'>
                  {userDisplayName(u)}
                </td>
                <td className='px-4 py-2'>{u.email ?? '—'}</td>
                <td className='px-4 py-2'>{u.phone_number ?? '—'}</td>
                <td className='px-4 py-2'>
                  {u.is_professional ? (
                    <span className='inline-flex px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 text-xs font-semibold'>
                      Yes
                    </span>
                  ) : (
                    <span className='text-gray-400 text-xs'>—</span>
                  )}
                </td>
                <td className='px-4 py-2'>
                  {u.calm_filter_enabled ? (
                    <span className='inline-flex px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 text-xs font-semibold'>
                      On
                    </span>
                  ) : (
                    <span className='text-gray-400 text-xs'>—</span>
                  )}
                </td>
                <td className='px-4 py-2 text-gray-500'>
                  {new Date(u.created_at).toLocaleDateString(undefined, {
                    timeZone: ADMIN_PACIFIC_TZ,
                  })}
                </td>
                <td className='px-4 py-2'>
                  {u.subscribed ? (
                    <span className='inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold'>
                      Yes{u.subscription_override ? ' (override)' : ''}
                    </span>
                  ) : (
                    <span className='text-gray-400 text-xs'>No</span>
                  )}
                </td>
                <td className='px-4 py-2'>
                  {u.deleted_at ? (
                    <span className='inline-flex px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold'>
                      Deleted
                    </span>
                  ) : (
                    <span className='text-gray-400 text-xs'>—</span>
                  )}
                </td>
                <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                  {u.household_message_count.toLocaleString()}
                </td>
                <td className='px-4 py-2 text-right tabular-nums text-gray-700'>
                  {u.household_expense_count.toLocaleString()}
                </td>
                <td className='px-4 py-2 text-right'>
                  <Link
                    to='/admin/users/$userId'
                    params={{ userId: String(u.id) }}
                    search={{ hid: undefined }}
                    className={USER_VIEW_LINK_CLASS}
                    onClick={(e) => {
                      e.stopPropagation();
                      track('Admin User Row Clicked', { source: 'table' });
                    }}
                  >
                    View
                  </Link>
                </td>
              </tr>
            ))}
            {data && data.users.length === 0 && !usersQuery.isLoading && (
              <tr>
                <td colSpan={12} className='px-4 py-8 text-center text-gray-400'>
                  No users match.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > 0 && (
        <div className='flex items-center justify-between text-sm text-gray-600'>
          <span>
            Showing {data.offset + 1}–
            {Math.min(data.offset + data.users.length, data.total)} of {data.total}
          </span>
          <div className='flex gap-2'>
            <button
              type='button'
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
            >
              Prev
            </button>
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <button
              type='button'
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= totalPages - 1}
              className='px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50'
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/')({
  component: AdminUsersPage,
});
